import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { CheckCircle2 } from 'lucide-react'
import { api } from '../lib/api'
import { useAuth } from './Auth'
import type { CartItem, Totals } from '../lib/types'

// Panier enregistré dans le navigateur ; les prix sont toujours recalculés par l'API.
const CART_KEY = 'cs_cart_v2'
const CODE_KEY = 'cs_promo_code_v2'
const ID_KEY = 'cs_cart_id'
// Stockage du navigateur : peut être bloqué (aperçu en iframe, navigation privée) ou contenir d'anciennes données
const readItems = (): CartItem[] => {
  try {
    const v = JSON.parse(localStorage.getItem(CART_KEY) || '[]')
    return Array.isArray(v) ? v.filter((i) => i && typeof i.id === 'string' && typeof i.key === 'string' && Number(i.qty) > 0).map((i) => ({ ...i, qty: Number(i.qty), variant: i.variant && typeof i.variant === 'object' ? i.variant : {} })) : []
  } catch {
    return []
  }
}
const readCode = (): string => {
  try {
    const v = JSON.parse(localStorage.getItem(CODE_KEY) || '""')
    return typeof v === 'string' ? v : ''
  } catch {
    return ''
  }
}
const save = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    /* stockage indisponible : le panier reste en mémoire */
  }
}
const EMPTY: Totals = { lines: [], count: 0, subtotal_ht: 0, promo: null, discount_ht: 0, shipping_ht: 0, free_shipping: false, remaining_for_free_shipping: 150, total_ht: 0, vat: 0, total_ttc: 0 }
const keyOf = (i: Omit<CartItem, 'key' | 'qty'>) => [i.id, JSON.stringify(i.variant), i.flocage ? 'F' : '', i.club].join('|')

type CartCtx = {
  items: CartItem[]; totals: Totals; code: string; open: boolean; getCartId: () => string
  add: (i: Omit<CartItem, 'key'>) => void; setQty: (key: string, qty: number) => void; clear: () => void
  setCode: (c: string) => void; setOpen: (o: boolean) => void; toast: (msg: string) => void
}
const Ctx = createContext<CartCtx>(null as unknown as CartCtx)

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(readItems)
  const [code, setCodeState] = useState<string>(readCode)
  const [totals, setTotals] = useState<Totals>(EMPTY)
  const [open, setOpen] = useState(false)
  const [message, setMessage] = useState('')
  const timer = useRef<number>(0)
  const saveTimer = useRef<number>(0)
  const cartId = useRef<string>((() => { try { return localStorage.getItem(ID_KEY) || '' } catch { return '' } })())
  const { user } = useAuth()
  const [restored, setRestored] = useState(() => !new URLSearchParams(window.location.search).get('reprise'))

  // Lien de relance : /panier?reprise=<id> remet le panier enregistré
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('reprise')
    if (!id) return
    api<{ cart_id: string; lines: Omit<CartItem, 'key'>[]; code: string }>(`/panier/reprise/${encodeURIComponent(id)}`)
      .then((c) => { cartId.current = c.cart_id; try { localStorage.setItem(ID_KEY, c.cart_id) } catch { /* */ } setItems(c.lines.map((l) => ({ ...l, key: keyOf(l) }))); setCodeState(c.code) })
      .catch(() => null)
      .finally(() => setRestored(true))
  }, [])

  // Enregistrement du panier côté serveur (paniers abandonnés), après une courte pause
  useEffect(() => {
    if (!restored || (!items.length && !cartId.current)) return
    clearTimeout(saveTimer.current)
    saveTimer.current = window.setTimeout(() => {
      api<{ cart_id: string }>('/panier/sauvegarde', 'POST', { cart_id: cartId.current, lines: items.map(({ key: _k, ...rest }) => rest), code })
        .then((r) => { cartId.current = r.cart_id; try { localStorage.setItem(ID_KEY, r.cart_id) } catch { /* stockage indisponible */ } })
        .catch(() => null)
    }, 1200)
  }, [items, code, restored, user ? user.id : ""])

  useEffect(() => {
    save(CART_KEY, items)
    save(CODE_KEY, code)
    if (!items.length) return setTotals(EMPTY)
    api<Totals>('/panier/calcul', 'POST', { lines: items.map(({ key: _k, ...rest }) => rest), code }).then(setTotals).catch(() => setTotals(EMPTY))
  }, [items, code])

  const toast = useCallback((msg: string) => {
    setMessage(msg)
    clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setMessage(''), 2600)
  }, [])
  const add = useCallback((i: Omit<CartItem, 'key'>) => {
    const key = keyOf(i)
    setItems((list) => (list.some((l) => l.key === key) ? list.map((l) => (l.key === key ? { ...l, qty: l.qty + i.qty } : l)) : [...list, { ...i, key }]))
  }, [])
  const setQty = useCallback((key: string, qty: number) => setItems((list) => list.map((l) => (l.key === key ? { ...l, qty } : l)).filter((l) => l.qty > 0)), [])
  const clear = useCallback(() => {
    cartId.current = ''
    try { localStorage.removeItem(ID_KEY) } catch { /* */ }
    setItems([])
    setCodeState('')
  }, [])

  return (
    <Ctx.Provider value={{ items, totals, code, open, getCartId: () => cartId.current, add, setQty, clear, setCode: setCodeState, setOpen, toast }}>
      {children}
      <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] pointer-events-none transition-all duration-300 ${message ? 'opacity-100' : 'opacity-0 translate-y-4'}`} data-testid="toast">
        <div className="flex items-center gap-2 bg-navy-900 text-white text-sm font-semibold px-5 py-3 rounded-xl shadow-2xl border border-slate-700">
          <CheckCircle2 className="w-5 h-5 text-brand-green" />{message}
        </div>
      </div>
    </Ctx.Provider>
  )
}

export const useCart = () => useContext(Ctx)
